/**
 * #8861 slice 1 — SAME-GROUPING MEASURES ARE ONE TABLE.
 *
 * "GL expense by function with budget and variance" computes three by_member
 * outputs over one population: actual, budget and variance. Drawn one list per
 * output, the reader rebuilds the table in their head; declared here, the
 * canvas draws one `decision_table` — a row per member, a column per measure.
 *
 * Declared only when nothing about the table is a guess:
 * - every output is by_member or rank, and its ledger entry names a governed
 *   measure label (the resolved family's catalog display name) and a
 *   population key (dataset revision, grouping and member filters): one key
 *   for all, a distinct label each;
 * - every figure is a coordinate row (claimRole 'row', or 'disclosure' for the
 *   figures the engine discloses beside them) over the same dimensions, and
 *   all of them share one period;
 * - rows join on binding coordinates, never on label text, and every row
 *   keeps one name that no other row shares.
 *
 * #8861 slice 3: when the plan groups by more than one dimension and every
 * figure carries its coordinates' governed names, the first planned dimension
 * names the row and the others become text columns headed by their dimension
 * display names ("Owner role", "Forecast finish"). Disclosed figures form their
 * own rows beneath, never summed with the rest.
 *
 * A member one measure lacks is an empty cell, never zero. Anything else
 * returns null, and the older declarations run exactly as before.
 */
import type { AskHelixNumericBindingDto } from '../../ask-helix';

type Binding = AskHelixNumericBindingDto;
type Name = Readonly<{ label: string; attributes: readonly string[] }>;
type Row = Readonly<{ key: string; name: Name; cells: readonly (Binding | null)[] }>;
type Naming = Readonly<{ attributeColumns: readonly string[]; nameOf: (binding: Binding) => Name }>;

/** One table row: its name, its attribute texts (slice 3) and a cell per measure. */
export type AskDecisionTableRow = Readonly<{
  label: string;
  attributes?: readonly string[];
  cells: readonly (string | null)[];
}>;

export type AskDecisionTableBody = Readonly<{
  kind: 'decision_table';
  subject: string | null;
  /** Published name of the dimension whose members name the rows. */
  rowHeader?: string;
  /** #8861 slice 3: headers of the text columns shown before the measures. */
  attributeColumns?: readonly string[];
  columns: readonly Readonly<{ outputId: string; label: string;
    periodBasis?: import('../../figure-period-basis').FigurePeriodBasis }>[];
  forecastStartColumnId?: string;
  rows: readonly AskDecisionTableRow[];
  /** #8861 slice 3: figures the engine discloses beside the rows, never summed with them. */
  disclosureRows?: readonly AskDecisionTableRow[];
}>;

/** What the declaration reads of one computed output. */
export type AskMeasureColumnSource = Readonly<{
  outputId: string;
  operation: string;
  label?: string;
  populationKey?: string;
  /** #8861 slice 3: the grouped dimensions in plan order, from the ledger. */
  groupBy?: readonly string[];
  bindings: readonly Binding[];
}>;

const GROUPED_OPERATIONS: ReadonlySet<string> = new Set(['by_member', 'rank']);
const TABLE_ROLES: ReadonlySet<string> = new Set(['row', 'disclosure']);

export function declareMeasureTable(
  outputs: readonly AskMeasureColumnSource[]
): AskDecisionTableBody | null {
  const labels = measureLabels(outputs);
  if (labels === null || !figuresShareRowShape(outputs.flatMap((output) => output.bindings))) {
    return null;
  }
  // Every measure owns at least one row; disclosed figures only ever sit beside rows.
  if (!outputs.every((output) => output.bindings.some((binding) => binding.claimRole === 'row'))) {
    return null;
  }
  const naming = namingFor(outputs);
  const rows = tableRows(outputs, 'row', naming);
  const disclosed = tableRows(outputs, 'disclosure', naming);
  if (rows === null || rows.length === 0 || disclosed === null) return null;
  const ordered = orderedRows(outputs, rows);
  if (ordered === null || !distinctNames([...rows, ...disclosed])) return null;
  const headers = columnHeaders(outputs, labels);
  return {
    kind: 'decision_table',
    subject: null,
    ...(naming.attributeColumns.length > 0 ? { attributeColumns: naming.attributeColumns } : {}),
    columns: outputs.map((output, index) => ({ outputId: output.outputId, label: headers[index] })),
    rows: ordered.map(tableRow),
    ...(disclosed.length > 0
      ? { disclosureRows: [...disclosed].sort(byFirstColumnThenLabel).map(tableRow) }
      : {}),
  };
}

function tableRow(row: Row): AskDecisionTableRow {
  return {
    label: row.name.label,
    ...(row.name.attributes.length > 0 ? { attributes: row.name.attributes } : {}),
    cells: row.cells.map((cell) => cell?.placeholder ?? null),
  };
}

/** The column labels, when every output is a labelled measure over one population. */
function measureLabels(outputs: readonly AskMeasureColumnSource[]): readonly string[] | null {
  if (outputs.length < 2 || !outputs.every((output) => GROUPED_OPERATIONS.has(output.operation))) {
    return null;
  }
  const labels = outputs.flatMap((output) => (output.label ? [output.label] : []));
  const keys = new Set(outputs.map((output) => output.populationKey ?? ''));
  if (labels.length !== outputs.length || new Set(labels).size !== labels.length) return null;
  return keys.size === 1 && !keys.has('') ? labels : null;
}

/**
 * #8861 slice 2: short headers. Each measure's governed output role (actual,
 * budget, variance: provider_relationships' outputRoleRef, carried on every
 * figure's meaning) names its column when every measure carries exactly one
 * role and no two share it. Otherwise the family labels stand. Never text
 * from the question.
 */
function columnHeaders(
  outputs: readonly AskMeasureColumnSource[],
  familyLabels: readonly string[]
): readonly string[] {
  const roles = outputs.map((output) => {
    const found = new Set(output.bindings.map(roleOf));
    return found.size === 1 ? [...found][0] : null;
  });
  const named = roles.every((role): role is string => role !== null);
  return named && new Set(roles).size === roles.length ? roles : familyLabels;
}

/** The figure's governed output role, read structurally (this module has no runtime-v3 import). */
function roleOf(binding: Binding): string | null {
  const role = (binding as Readonly<{ meaning?: Readonly<{ outputRole?: unknown }> }>).meaning
    ?.outputRole;
  return typeof role === 'string' && role !== '' ? role : null;
}

/**
 * #8861 slice 3: name rows by the plan's first dimension and show the rest as
 * attribute columns, when every output shares one multi-dimension plan order
 * and every figure carries all its coordinates' governed names under one set
 * of dimension names. Otherwise each row keeps its one published label.
 */
function namingFor(outputs: readonly AskMeasureColumnSource[]): Naming {
  const plain: Naming = {
    attributeColumns: [],
    nameOf: (binding) => ({ label: memberLabel(binding), attributes: [] }),
  };
  const orders = new Set(outputs.map((output) => JSON.stringify(output.groupBy ?? [])));
  const groupBy = outputs[0]?.groupBy ?? [];
  if (orders.size !== 1 || groupBy.length < 2) return plain;
  const bindings = outputs.flatMap((output) => output.bindings);
  const named = bindings.map((binding) => namesByDimension(binding, groupBy));
  if (!named.every((names): names is ReadonlyMap<string, CoordinateName> => names !== null)) {
    return plain;
  }
  const headers = groupBy
    .slice(1)
    .map((ref) => new Set(named.map((names) => names.get(ref)?.dimension)));
  if (!headers.every((set) => set.size === 1)) return plain;
  const byBinding = new Map(bindings.map((binding, index) => [binding, named[index]] as const));
  return {
    attributeColumns: headers.map((set) => [...set][0] as string),
    nameOf: (binding) => {
      const names = byBinding.get(binding) as ReadonlyMap<string, CoordinateName>;
      return {
        label: names.get(groupBy[0])?.member ?? memberLabel(binding),
        attributes: groupBy.slice(1).map((ref) => names.get(ref)?.member ?? ''),
      };
    },
  };
}

type CoordinateName = Readonly<{ dimension: string; member: string }>;

/** The figure's coordinate names by dimension, when they cover exactly the planned dimensions. */
function namesByDimension(
  binding: Binding,
  groupBy: readonly string[]
): ReadonlyMap<string, CoordinateName> | null {
  const listed = (binding as Readonly<{ coordinateLabels?: unknown }>).coordinateLabels;
  if (!Array.isArray(listed)) return null;
  const names = new Map<string, CoordinateName>();
  for (const entry of listed as readonly Readonly<Record<string, unknown>>[]) {
    const { dimensionRef, dimension, member } = entry ?? {};
    if (
      typeof dimensionRef !== 'string' ||
      typeof dimension !== 'string' ||
      typeof member !== 'string'
    ) {
      return null;
    }
    if (dimension === '' || member === '') return null;
    names.set(dimensionRef, { dimension, member });
  }
  const coordinates = Object.keys(binding.coordinates ?? {});
  const covered =
    groupBy.every((ref) => names.has(ref)) && coordinates.every((ref) => groupBy.includes(ref));
  return covered && coordinates.length === groupBy.length ? names : null;
}

/** Every figure is a coordinate row over the same dimensions, all for one period. */
function figuresShareRowShape(bindings: readonly Binding[]): boolean {
  const [first] = bindings;
  if (first === undefined) return false;
  return bindings.every(
    (binding) =>
      TABLE_ROLES.has(binding.claimRole ?? '') &&
      dimensionsOf(binding) === dimensionsOf(first) &&
      periodOf(binding) === periodOf(first)
  );
}

/**
 * The rows of one claim role, a row per member across every measure. Null when
 * a member repeats inside one measure, or one member carries two names.
 */
function tableRows(
  outputs: readonly AskMeasureColumnSource[],
  role: string,
  naming: Naming
): readonly Row[] | null {
  const columns = outputs.map((output) =>
    byCoordinates(output.bindings.filter((binding) => binding.claimRole === role))
  );
  if (!columns.every((column): column is ReadonlyMap<string, Binding> => column !== null)) {
    return null;
  }
  const keys = [...new Set(columns.flatMap((column) => [...column.keys()]))];
  const rows = keys.map((key): Row | null => {
    const cells = columns.map((column) => column.get(key) ?? null);
    const names = new Map(
      cells
        .flatMap((cell) => (cell === null ? [] : [naming.nameOf(cell)]))
        .map((name) => [JSON.stringify(name), name] as const)
    );
    return names.size === 1 ? { key, name: [...names.values()][0], cells } : null;
  });
  return rows.every((row): row is Row => row !== null) ? rows : null;
}

/** One output's figures by coordinates; null when a member repeats (never pick one). */
function byCoordinates(bindings: readonly Binding[]): ReadonlyMap<string, Binding> | null {
  const keyed = new Map(bindings.map((binding) => [coordinateKey(binding), binding] as const));
  return keyed.size === bindings.length ? keyed : null;
}

/** Two members shown under one name could not be told apart by the reader. */
function distinctNames(rows: readonly Row[]): boolean {
  return new Set(rows.map((row) => JSON.stringify(row.name))).size === rows.length;
}

/**
 * A rank output keeps the engine's order, and must then cover every row: no
 * ranked figure is dropped and no unranked member is slotted in. Otherwise
 * the first column's value orders the rows, largest first, then the label.
 */
function orderedRows(
  outputs: readonly AskMeasureColumnSource[],
  rows: readonly Row[]
): readonly Row[] | null {
  const ranked = outputs.find((output) => output.operation === 'rank');
  if (ranked === undefined) return [...rows].sort(byFirstColumnThenLabel);
  if (rows.some((row) => row.cells.includes(null))) return null;
  const order = ranked.bindings.map(coordinateKey);
  return [...rows].sort((a, b) => order.indexOf(a.key) - order.indexOf(b.key));
}

function byFirstColumnThenLabel(a: Row, b: Row): number {
  const difference = (b.cells[0]?.value ?? -Infinity) - (a.cells[0]?.value ?? -Infinity);
  return Number.isNaN(difference) || difference === 0
    ? compareText(a.name.label, b.name.label)
    : difference;
}

/** The same label rule as `declared-exhibit.ts`'s member label. */
export function memberLabel(binding: Binding): string {
  return binding.displayLabel ?? binding.scope.label;
}

export function coordinateKey(binding: Binding): string {
  const entries = Object.entries(binding.coordinates ?? {});
  return JSON.stringify(entries.sort(([a], [b]) => compareText(a, b)));
}

function dimensionsOf(binding: Binding): string {
  return JSON.stringify(Object.keys(binding.coordinates ?? {}).sort(compareText));
}

function periodOf(binding: Binding): string {
  const { kind, label, asOf, start, end } = binding.timeframe;
  return JSON.stringify([kind, label, asOf ?? null, start ?? null, end ?? null]);
}

export function compareText(a: string, b: string): number {
  if (a === b) return 0;
  return a < b ? -1 : 1;
}
